import React, { useMemo, useState } from 'react';
import ToolLayout from 'tools/ToolLayout';
import {
  btnRow,
  card,
  chipStyle,
  errorBox,
  hint,
  label,
  primaryBtn,
  privacy,
} from 'tools/toolStyles';
import { downloadBlob, getWin, replaceExt } from 'tools/ffmpegShared';

type SourceEnc = 'utf-8' | 'windows-1258' | 'windows-1252';
type OutEnc = 'utf-8' | 'utf-8-bom';
type LineEnd = 'lf' | 'crlf';
type Action = 'raw' | 'csv2json' | 'json2csv' | 'json-pretty' | 'json-minify';

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\r') {
      /* skip */
    } else if (ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell.length || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.length > 1 || (r[0] && r[0].trim() !== ''));
}

function csvToJson(text: string): string {
  const rows = parseCsv(text);
  if (!rows.length) return '[]';
  const headers = rows[0].map((h, i) => (h && h.trim() ? h.trim() : `col${i + 1}`));
  const data = rows.slice(1).map((r) => {
    const obj: { [k: string]: string } = {};
    headers.forEach((h, i) => {
      obj[h] = r[i] != null ? r[i] : '';
    });
    return obj;
  });
  return JSON.stringify(data, null, 2);
}

function jsonToCsv(text: string): string {
  const data = JSON.parse(text);
  if (!Array.isArray(data) || !data.length) return '';
  const keys: string[] = [];
  data.forEach((row: any) => {
    if (row && typeof row === 'object') {
      Object.keys(row).forEach((k) => {
        if (keys.indexOf(k) < 0) keys.push(k);
      });
    }
  });
  const esc = (v: any) => {
    const s = v == null ? '' : String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [keys.map(esc).join(',')];
  data.forEach((row: any) => {
    lines.push(keys.map((k) => esc(row ? row[k] : '')).join(','));
  });
  return lines.join('\n');
}

function applyLineEndings(text: string, mode: LineEnd): string {
  const norm = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return mode === 'crlf' ? norm.replace(/\n/g, '\r\n') : norm;
}

function encodeOut(text: string, enc: OutEnc): any {
  const win = getWin();
  const encoder = new win.TextEncoder();
  const body = encoder.encode(text);
  if (enc === 'utf-8-bom') {
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const merged = new Uint8Array(bom.length + body.length);
    merged.set(bom, 0);
    merged.set(body, bom.length);
    return new win.Blob([merged], { type: 'text/plain;charset=utf-8' });
  }
  return new win.Blob([body], { type: 'text/plain;charset=utf-8' });
}

const ConvertText = () => {
  const [file, setFile] = useState<any>(null);
  const [sourceEnc, setSourceEnc] = useState<SourceEnc>('utf-8');
  const [outEnc, setOutEnc] = useState<OutEnc>('utf-8');
  const [lineEnd, setLineEnd] = useState<LineEnd>('lf');
  const [action, setAction] = useState<Action>('raw');
  const [preview, setPreview] = useState('');
  const [resultText, setResultText] = useState('');
  const [outName, setOutName] = useState('');
  const [error, setError] = useState('');

  const win = getWin();

  const onPick = async (e: any) => {
    const f = e.target && e.target.files && e.target.files[0];
    if (!f) return;
    setFile(f);
    setError('');
    setResultText('');
    try {
      const buf = await f.arrayBuffer();
      const decoder = new win.TextDecoder(sourceEnc, { fatal: false });
      const text = decoder.decode(buf);
      setPreview(text.slice(0, 800));
      (f as any).__decoded = text;
    } catch (err: any) {
      setError((err && err.message) || 'Không đọc được file.');
    }
  };

  const reloadWithEnc = async (enc: SourceEnc) => {
    setSourceEnc(enc);
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const decoder = new win.TextDecoder(enc, { fatal: false });
      const text = decoder.decode(buf);
      setPreview(text.slice(0, 800));
      (file as any).__decoded = text;
      setError('');
    } catch (err: any) {
      setError((err && err.message) || 'Không đọc được với encoding này.');
    }
  };

  const outExt = useMemo(() => {
    if (action === 'csv2json' || action === 'json-pretty' || action === 'json-minify') return 'json';
    if (action === 'json2csv') return 'csv';
    return extFromFile(file);
  }, [action, file]);

  const run = () => {
    if (!file) {
      setError('Hãy chọn file văn bản.');
      return;
    }
    setError('');
    try {
      let text = (file as any).__decoded;
      if (typeof text !== 'string') throw new Error('Chưa đọc được nội dung file.');

      if (action === 'csv2json') {
        text = csvToJson(text);
      } else if (action === 'json2csv') {
        text = jsonToCsv(text);
      } else if (action === 'json-pretty') {
        text = JSON.stringify(JSON.parse(text), null, 2);
      } else if (action === 'json-minify') {
        text = JSON.stringify(JSON.parse(text));
      }

      text = applyLineEndings(text, lineEnd);
      setResultText(text);
      setOutName(replaceExt(file.name, outExt));
    } catch (err: any) {
      setError((err && err.message) || 'Chuyển đổi thất bại.');
      setResultText('');
    }
  };

  const download = () => {
    if (!resultText) return;
    const blob = encodeOut(resultText, outEnc);
    downloadBlob(blob, outName || 'output.txt');
  };

  return (
    <ToolLayout title="Đổi đuôi văn bản">
      <p style={privacy}>
        Văn bản được xử lý hoàn toàn trên máy bạn (thuần JavaScript), không gửi lên server language123.net.
      </p>

      <div style={card}>
        <label style={label}>1. Chọn file</label>
        <p style={hint}>TXT / CSV / JSON / XML / LOG / MD</p>
        <input type="file" accept=".txt,.csv,.json,.xml,.log,.md,text/*,application/json" onChange={onPick} />
        {file ? <p style={{ ...hint, marginTop: 8, marginBottom: 0 }}>{file.name}</p> : null}
      </div>

      <div style={card}>
        <label style={label}>2. Encoding nguồn (khi đọc)</label>
        <div style={btnRow}>
          <button type="button" style={chipStyle(sourceEnc === 'utf-8')} onClick={() => reloadWithEnc('utf-8')}>
            UTF-8
          </button>
          <button type="button" style={chipStyle(sourceEnc === 'windows-1258')} onClick={() => reloadWithEnc('windows-1258')}>
            Windows-1258 (VN)
          </button>
          <button type="button" style={chipStyle(sourceEnc === 'windows-1252')} onClick={() => reloadWithEnc('windows-1252')}>
            Windows-1252
          </button>
        </div>
        {preview ? (
          <>
            <label style={label}>Xem trước (đầu file)</label>
            <pre
              style={{
                margin: 0,
                padding: 10,
                background: '#f5f5f5',
                borderRadius: 8,
                fontSize: 12,
                maxHeight: 160,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {preview}
            </pre>
          </>
        ) : null}
      </div>

      <div style={card}>
        <label style={label}>3. Thao tác</label>
        <div style={btnRow}>
          <button type="button" style={chipStyle(action === 'raw')} onClick={() => setAction('raw')}>
            Giữ nội dung
          </button>
          <button type="button" style={chipStyle(action === 'csv2json')} onClick={() => setAction('csv2json')}>
            CSV → JSON
          </button>
          <button type="button" style={chipStyle(action === 'json2csv')} onClick={() => setAction('json2csv')}>
            JSON → CSV
          </button>
          <button type="button" style={chipStyle(action === 'json-pretty')} onClick={() => setAction('json-pretty')}>
            JSON pretty
          </button>
          <button type="button" style={chipStyle(action === 'json-minify')} onClick={() => setAction('json-minify')}>
            JSON minify
          </button>
        </div>

        <label style={label}>Xuất encoding</label>
        <div style={btnRow}>
          <button type="button" style={chipStyle(outEnc === 'utf-8')} onClick={() => setOutEnc('utf-8')}>
            UTF-8
          </button>
          <button type="button" style={chipStyle(outEnc === 'utf-8-bom')} onClick={() => setOutEnc('utf-8-bom')}>
            UTF-8 BOM
          </button>
        </div>

        <label style={label}>Xuống dòng</label>
        <div style={btnRow}>
          <button type="button" style={chipStyle(lineEnd === 'lf')} onClick={() => setLineEnd('lf')}>
            LF (Unix)
          </button>
          <button type="button" style={chipStyle(lineEnd === 'crlf')} onClick={() => setLineEnd('crlf')}>
            CRLF (Windows)
          </button>
        </div>
      </div>

      <div style={card}>
        <div style={btnRow}>
          <button type="button" style={primaryBtn} disabled={!file} onClick={run}>
            Chuyển đổi
          </button>
          {resultText ? (
            <button type="button" style={primaryBtn} onClick={download}>
              Tải {outName || 'file'}
            </button>
          ) : null}
        </div>
        {error ? <p style={errorBox}>{error}</p> : null}
        {resultText ? (
          <>
            <label style={label}>Kết quả (xem trước)</label>
            <pre
              style={{
                margin: 0,
                padding: 10,
                background: '#e8f5e9',
                borderRadius: 8,
                fontSize: 12,
                maxHeight: 220,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {resultText.slice(0, 2000)}
              {resultText.length > 2000 ? '\n…' : ''}
            </pre>
          </>
        ) : null}
      </div>
    </ToolLayout>
  );
};

function extFromFile(file: any): string {
  if (!file || !file.name) return 'txt';
  const m = /\.([a-z0-9]+)$/i.exec(file.name);
  return m ? m[1].toLowerCase() : 'txt';
}

export default ConvertText;
